import React, { useEffect, useState } from "react";
import { getStorage, ref, getDownloadURL, listAll } from "firebase/storage";
import {
  collection,
  getDocs,
  getFirestore,
  query,
  orderBy,
  limit,
  getDoc,
  doc,
} from "firebase/firestore";

import "../firebase";
import "../styles/home.css";
import NavBar from "./NavBar";
import SkeletonCard from "./SkeletonCard";
import Footer from "./Footer";
import { Link } from "react-router-dom";
import ProductImageSlideshow from "./ProductImageSlideshow";

import coverImage from "../assets/coverImage.webp";

function Home() {
  const [coverImages, setCoverImages] = useState([
    { id: "fallback", imgUrl: coverImage },
  ]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [featureCollection, setFeatureCollection] = useState([]);
  const [fesiCollection, setFesiCollection] = useState([]);
  const [resinCollection, setResinCollection] = useState([]);
  const [collectionData, setCollectionData] = useState([]);
  const [premiumItem, setPremiumItem] = useState([]);
  const [loading, setLoading] = useState(true);

  const db = getFirestore();
  const storage = getStorage();

  // ✅ Load cover images from Firebase Storage
  useEffect(() => {
    const coverImgsRef = ref(storage, "coverImages");

    listAll(coverImgsRef)
      .then((res) => {
        const promises = res.items.map((itemRef) => getDownloadURL(itemRef));

        Promise.all(promises)
          .then((urls) => {
            const coverImgs = res.items.map((itemRef, index) => ({
              id: itemRef.name,
              imgUrl: urls[index],
            }));

            setCoverImages((prev) => {
              const fallback = prev.find((img) => img.id === "fallback");
              const merged = fallback ? [fallback, ...coverImgs] : coverImgs;
              return merged;
            });
          })
          .catch((error) => {
            console.error("Error fetching download URLs: ", error);
          });
      })
      .catch((error) => {
        console.error("Error listing images: ", error);
      });
  }, [storage]);

  // ✅ Fetch all product collections
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const collections = [
          "newArrivals",
          "featureCollection",
          "fesiCollection",
          "resinCollection",
          "premiumItem",
        ];

        const promises = collections.map((name) =>
          getDocs(collection(db, name))
        );
        const [newArrivalsSnap, featureSnap, fesiSnap, resinSnap, premiumSnap] =
          await Promise.all(promises);

        const sortData = (docs) => {
          const allDocs = docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          const filterDoc = allDocs.find((item) => item.id === "productFilter");
          const sortOrder = filterDoc?.type || "newest";

          const items = allDocs.filter(
            (item) => item.id !== "heading" && item.id !== "productFilter"
          );

          return items.sort((a, b) => {
            const priceA = parseFloat(a.productPrice);
            const priceB = parseFloat(b.productPrice);
            if (sortOrder === "lowToHigh") return priceA - priceB;
            if (sortOrder === "highToLow") return priceB - priceA;
            if (sortOrder === "newest")
              return b.createdAt?.seconds - a.createdAt?.seconds;
            if (sortOrder === "oldest")
              return a.createdAt?.seconds - b.createdAt?.seconds;
            return 0;
          });
        };

        setNewArrivals(sortData(newArrivalsSnap.docs));
        setFeatureCollection(sortData(featureSnap.docs));
        setFesiCollection(sortData(fesiSnap.docs));
        setResinCollection(sortData(resinSnap.docs));
        setPremiumItem(
          premiumSnap.docs
            .filter((doc) => doc.id !== "heading" && doc.id !== "productFilter")
            .map((doc) => ({ id: doc.id, ...doc.data() }))
        );

        setLoading(false);
      } catch (error) {
        console.error("Error fetching collections: ", error);
      }
    };

    fetchCollections();
  }, [db]);

  // ✅ Fetch "collection_1" ... "collection_5" (for the Collections section)
  useEffect(() => {
    const fetchCollectionData = async () => {
      try {
        const collections = [
          "collection_1",
          "collection_2",
          "collection_3",
          "collection_4",
          "collection_5",
        ];

        const promises = collections.map(async (collectionName) => {
          const headingDocRef = doc(db, collectionName, "heading");
          const headingDoc = await getDoc(headingDocRef);
          const heading = headingDoc.exists()
            ? headingDoc.data().heading
            : null;

          const productQuery = query(
            collection(db, collectionName),
            orderBy("productImage"),
            limit(1)
          );
          const productSnapshot = await getDocs(productQuery);
          const latestProductDoc = productSnapshot.docs[0];
          const collectionImage = latestProductDoc
            ? latestProductDoc.data().productImage
            : null;

          return { heading, collectionImage };
        });

        const data = await Promise.all(promises);
        setCollectionData(data);
      } catch (error) {
        console.error("Error fetching collection data: ", error);
      }
    };

    fetchCollectionData();
  }, [db]);

  // ✅ Convert Firebase image URL → ImageKit optimized URL
  const transformToImageKit = (
    firebaseUrl,
    transform = "tr=w-400,q-30,f-auto"
  ) => {
    const base = "https://ik.imagekit.io/fesirh110/";
    const decoded = decodeURIComponent(firebaseUrl);
    const match = decoded.match(/%2F(.+?)\?alt=media/);
    return match ? `${base}${match[1]}?${transform}` : firebaseUrl;
  };

  return (
    <div className="main-container">
      <div className="announcement-bar">
        بِسْمِ اللهِ الرَّحْمٰنِ الرَّحِيْمِ (ِنَّ اللَّهَ يَرْزُقُ مَن يَشَاءُ
        بِغَيْرِ حِسَابٍ)
      </div>

      <NavBar />

      {/* ✅ Carousel Section */}
      <div
        id="carouselExampleIndicators"
        className="carousel slide carousel-fade bg-black"
        data-bs-ride="carousel"
      >
        <div className="carousel-indicators">
          {coverImages.map((_, index) => (
            <button
              key={index}
              type="button"
              data-bs-target="#carouselExampleIndicators"
              data-bs-slide-to={index}
              className={index === 0 ? "active" : ""}
              aria-current={index === 0 ? "true" : ""}
              aria-label={`Slide ${index + 1}`}
            ></button>
          ))}
        </div>
        <div className="carousel-inner">
          {coverImages.map((image, index) => (
            <div
              key={image.id}
              className={`carousel-item ${index === 0 ? "active" : ""}`}
            >
              <img
                src={image.imgUrl}
                className="d-block w-100"
                alt={image.id}
              />
            </div>
          ))}
        </div>
        <button
          className="carousel-control-prev"
          type="button"
          data-bs-target="#carouselExampleIndicators"
          data-bs-slide="prev"
        >
          <span
            className="carousel-control-prev-icon"
            aria-hidden="true"
          ></span>
          <span className="visually-hidden">Previous</span>
        </button>
        <button
          className="carousel-control-next"
          type="button"
          data-bs-target="#carouselExampleIndicators"
          data-bs-slide="next"
        >
          <span
            className="carousel-control-next-icon"
            aria-hidden="true"
          ></span>
          <span className="visually-hidden">Next</span>
        </button>
      </div>

      {[
        { title: "New Arrivals", data: newArrivals, path: "newArrivals" },
        {
          title: "Feature Collection",
          data: featureCollection,
          path: "featureCollection",
        },
        {
          title: "Fesi Collection",
          data: fesiCollection,
          path: "fesiCollection",
        },
        {
          title: "Resin Collection",
          data: resinCollection,
          path: "resinCollection",
        },
      ].map((section, index) => (
        <div className="container text-center my-5" key={index}>
          <h2>{loading ? section.title : section.title}</h2>
          <div className="row justify-content-center gx-2 gy-3">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))
              : section.data.map((item) => (
                  <div className="col-6 col-md-4 col-lg-3 mb-2" key={item.id}>
                    <div className="card border-0 p-2">
                      <Link
                        to={`/product/${section.path}/${item.id}`}
                        state={{ product: item, collectionName: section.path }}
                        className="link"
                      >
                        <ProductImageSlideshow
                          item={item}
                          alt={item.productTitle || `Product`}
                        />
                        <div className="card-body p-2">
                          <h5 className="card-title text-start">
                            {item.productTitle}
                          </h5>
                          <p className="card-text text-start">
                            Rs {item.productPrice} PKR
                            {item.oldPrice && (
                              <span className="d-block card-text text-start old_price">
                                Rs {item.oldPrice} PKR
                              </span>
                            )}
                          </p>
                        </div>
                      </Link>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      ))}

      {/* ✅ Collections Section */}
      <div className="feature-collection container my-3 mb-3">
        <h2>Collections</h2>
        <div className="row justify-content-center gx-2 mt-4">
          {loading
            ? Array.from({ length: 5 }).map((_, index) => (
                <SkeletonCard key={index} />
              ))
            : collectionData.map((collection, index) => (
                <div className="col-6 col-md-4 col-lg-3 mb-4" key={index}>
                  <div className="card border-0 p-2">
                    <Link
                      to={`/collection/collection_${index + 1}`}
                      state={{ heading: collection.heading }}
                      className="link"
                    >
                      <img
                        loading="lazy"
                        src={collection.collectionImage}
                        className="card-img-top card-img-custom"
                        alt={`Collection ${index + 1}`}
                      />
                      <div className="card-body p-2">
                        <h5 className="card-title text-start">
                          {collection.heading}
                        </h5>
                      </div>
                    </Link>
                  </div>
                </div>
              ))}
        </div>
      </div>

      {/* ✅ Premium Item Section */}
      <div className="premium-item container-md mt-5 mb-3">
        <div className="row justify-content-center mt-4">
          {premiumItem.map((item) => (
            <div className="col-12 mb-4" key={item.id}>
              <div className="card border-0 p-1 m-0">
                <div className="d-flex flex-column flex-lg-row align-items-center">
                  <div className="flex-lg-1 w-100 w-lg-50 text-center text-lg-start mb-3 mb-lg-0">
                    <img
                      loading="lazy"
                      src={transformToImageKit(item.productImage)}
                      className="premium-img rounded img-fluid"
                      alt={item.productTitle}
                    />
                  </div>
                  <div className="flex-lg-1 w-100 w-lg-50 card-body card-body-custom">
                    <h2 className="card-title mb-4 fs-2">
                      {item.productTitle}
                    </h2>
                    <p className="card-text mb-5">{item.productDescription}</p>
                    <a
                      href="/"
                      type="button"
                      className="btn btn-custom btn-outline-secondary"
                    >
                      SHOP NOW
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Home;
